import { Component, OnInit } from '@angular/core';
import { combineLatest, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { PreChatService } from '../services/pre-chat.service';
import { DisplaySetting } from '../models/display-setting';
import { SkillAvailability } from '../models/skill-availability';
import { CHAT_TYPE } from '../models/chat-type-model';

@Component({
    selector: 'pre-chat-root',
    templateUrl: './pre-chat.component.html',
    styleUrls: ['./pre-chat.component.scss']
})

export class PreChatComponent implements OnInit {

    reCaptchaEnabled: boolean = model.recaptchaEnabled;
    reCaptchaSiteKey: string = model.reCaptchaSiteKey;
    error: string = model.error;
    language: string = model.userLanguage;
    formTag: string;
    formType: CHAT_TYPE;
    timezone: string = "";
    displaySettingId: number = parseInt(model.displaySettingId);
    resources = resources;
    model = model;
    skillAvailabilityList: SkillAvailability[];
    settings: any = null;
    form: any;
    chatSessionId: string;
    location: Location;
    skillAvailabilityCheckTimer: any;
    skillAvailabilityCheckSeconds: number = parseInt(model.skillAvailabilityCheckSeconds);
    isBrowserSupported: boolean = model.isBrowserSupported;

    constructor(private preChatService: PreChatService) {
        this.preChatService.activeChatData$.subscribe(activeChatData => {
            if (activeChatData.formTag) {
                this.formTag = activeChatData.formTag;
            } else {
                this.formTag = null;
            }

            if (activeChatData.location) {
                this.location = activeChatData.location;
            }

            this.formType = activeChatData.formType;
            this.chatSessionId = activeChatData.chatSessionId;
            this.loadForm();
        });

        this.preChatService.errorMessage$.subscribe(text => {
            this.error = text;//set the error message
        });
    }

    ngOnInit(): void {
        console.info('[PreChatComponent] Initializing.');
        this.language = this.language || window.navigator.language || "en";
        upstream.customerchat.helpers.setPreferredLanguageCss(this.language);
        this.timezone = this.preChatService.getTimeZone();
        this.preChatService.sendChatFrameLoadEvent();

        if (!this.isBrowserSupported) {
            console.warn("This browser version is not supported.");
        }
    }

    loadForm() {
        console.info('[PreChatComponent] Loading form', { type: CHAT_TYPE[this.formType], tag: this.formTag });

        this.settings = null;
        let form$: Observable<any> = this.preChatService.getPreChatForm(this.language, this.formTag).pipe(
            catchError(err => {
                console.error(`Failed to load pre chat form with language '${this.language}' and formTag '${this.formTag}'`);
                return throwError(err);
            }),
            tap(() => this.logWithTimestamp('Init', 'Successfully fetched pre chat form'))
        );
        let displaySetting$: Observable<DisplaySetting> = this.preChatService.getDisplaySetting(this.displaySettingId).pipe(
            catchError(err => {
                console.error(`Failed to load display setting with id '${this.displaySettingId}'`);
                return throwError(err);
            }),
            tap(() => this.logWithTimestamp('Init', 'Successfully fetched display setting'))
        );

        combineLatest(form$, displaySetting$).subscribe({
            next: data => {
                let [form, displaySetting] = data;

                // Get list of skill addresses configured in the pre-chat form
                let skillField = form.rows.find(row => row.fieldTypeName === 'Skill Dropdown' && row.mappingFieldName === 'Source');
                // Use drop down options OR empty skill, any overidden skill or default skill address is handled in chat engine
                let skillAddressesFromPreChatForm = skillField
                    ? skillField.options.map(option => option.valueCode)
                    : this.model.targetSkillAddress
                        ? [this.model.targetSkillAddress]
                        : [];
                // send list of skills so that we can a skill Availability check
                this.logWithTimestamp('Init', 'Send Prechat Form Ready event');
                this.preChatService.sendPreChatFormReadyEvent(skillAddressesFromPreChatForm, this.formType);

                this.form = form;
                this.settings = {
                    form,
                    displaySetting,
                    formTag: this.formTag,
                    formType: this.formType,
                    chatSessionId: this.chatSessionId,
                    sourceUrl: this.location.pathname
                };

                if (this.formType === CHAT_TYPE.REACTIVE) {
                    if (this.formTag !== null && form.tag !== this.formTag) {
                        console.warn(`Unable to find form with tag '${this.formTag}' so loading default form with Id 1`);
                    }
                    //TODO Handle scenario when error occurs on submitting Reactive chat in case there is no form
                    if (this.formTag === null && this.error) {
                        model.error = this.error = null;
                        console.error("Error occurred while creating reactive chat");
                    }
                } else if (this.formType === CHAT_TYPE.PROACTIVE && this.error && form.tag !== this.formTag) {
                    //Send error response to proactive engine for displaying error when no form is provided for Proactive
                    this.preChatService.sendProactiveErrorMessage(this.error);
                    //since error will be handled by proactive engine hence setting it null here
                    model.error = this.error = null;
                }
            },
            error: err => {
                console.error(err);
            }
        });
    }

    logWithTimestamp(tag: string, msg: string) {
        console.debug(tag + ': ' + msg + ' at ' + new Date().toLocaleTimeString());
    }
}
